import React from 'react';
import './DataTable.css';

/*
 * DataTable — presentational table (data passed in; no fetching/sorting logic).
 * The mock's `.tbl` inside a `.table-wrap` (sticky header, hover, selected row,
 * zebra, tabular-nums).
 *
 * Props:
 *   columns : [{
 *     key,                 // unique; also default row-value accessor
 *     header,              // column label (node)
 *     align,               // 'left' | 'right' | 'center'
 *     mono,                // render cell in monospace + tabular-nums
 *     num,                 // tabular-nums (right-aligned numbers)
 *     width,               // CSS width for the column
 *     className,           // extra class on <td>
 *     headerClassName,     // extra class on <th>
 *     render(row, i)       // custom cell renderer (falls back to row[key])
 *   }]
 *   rows        : array of row objects
 *   rowKey      : (row, i) => key            (default i)
 *   onRowClick  : (row, i) => void           (rows become clickable)
 *   selectedKey : key of the selected row (highlights it)
 *   isSelected  : (row, i) => boolean        (alternative to selectedKey)
 *   empty       : node shown when rows is empty (spans all columns)
 *   maxHeight   : CSS max-height on the scroll wrapper (enables sticky header)
 *   dense       : tighter row padding
 */
export default function DataTable({
  columns = [],
  rows = [],
  rowKey,
  onRowClick,
  selectedKey,
  isSelected,
  empty = 'No records.',
  maxHeight,
  dense = false,
  className = '',
  wrapClassName = '',
  sortKey,
  sortDir,
  onSort,
  onScrollEnd,          // ()=>void — fired near the scroll bottom (infinite scroll)
  loadingMore = false,  // show a "loading more…" row at the table bottom
  ...rest
}) {
  const clickable = typeof onRowClick === 'function';

  // infinite scroll: fire onScrollEnd when the wrap is scrolled near its bottom, and
  // an auto-fill so a short first page (no scrollbar) still pulls the next page.
  const wrapRef = React.useRef(null);
  const handleScroll = () => {
    const el = wrapRef.current;
    if (!el || !onScrollEnd) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 64) onScrollEnd();
  };
  React.useEffect(() => {
    if (!onScrollEnd || loadingMore) return;
    const el = wrapRef.current;
    if (el && el.scrollHeight <= el.clientHeight) onScrollEnd();
  }, [onScrollEnd, loadingMore, rows.length]);

  const cellClass = (col) => [
    col.mono ? 'mono' : '',
    col.num ? 'num' : '',
    col.align === 'right' ? 'ta-r' : col.align === 'center' ? 'ta-c' : '',
    col.className || '',
  ].filter(Boolean).join(' ');

  const rowSelected = (row, i) => {
    if (typeof isSelected === 'function') return isSelected(row, i);
    if (selectedKey == null) return false;
    const k = rowKey ? rowKey(row, i) : i;
    return k === selectedKey;
  };

  return (
    <div
      ref={wrapRef}
      onScroll={onScrollEnd ? handleScroll : undefined}
      className={`table-wrap ui-datatable${wrapClassName ? ' ' + wrapClassName : ''}`}
      style={maxHeight ? { maxHeight } : undefined}
    >
      <table className={`tbl${dense ? ' tbl--dense' : ''}${className ? ' ' + className : ''}`} {...rest}>
        <thead>
          <tr>
            {columns.map((col) => {
              const canSort = col.sortable && typeof onSort === 'function';
              const active = canSort && sortKey === col.key;
              return (
                <th
                  key={col.key}
                  className={[
                    col.align === 'right' ? 'ta-r' : col.align === 'center' ? 'ta-c' : '',
                    canSort ? 'th-sortable' : '',
                    col.headerClassName || '',
                  ].filter(Boolean).join(' ')}
                  style={col.width ? { width: col.width } : undefined}
                  scope="col"
                  aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  {canSort ? (
                    <button type="button" className={`th-sort${active ? ' is-active' : ''}`} onClick={() => onSort(col.key)}>
                      <span>{col.header}</span>
                      <span className="th-sort__arrow" aria-hidden="true">{active ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}</span>
                    </button>
                  ) : col.header}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr className="ui-datatable__emptyrow">
              <td colSpan={columns.length || 1}>
                <div className="ui-datatable__empty">{empty}</div>
              </td>
            </tr>
          ) : (
            rows.map((row, i) => {
              const key = rowKey ? rowKey(row, i) : i;
              const sel = rowSelected(row, i);
              return (
                <tr
                  key={key}
                  className={clickable ? 'ui-datatable__row--clickable' : undefined}
                  aria-selected={sel || undefined}
                  onClick={clickable ? () => onRowClick(row, i) : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onKeyDown={clickable ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(row, i); }
                  } : undefined}
                >
                  {columns.map((col) => (
                    <td key={col.key} className={cellClass(col)}>
                      {col.render ? col.render(row, i) : row[col.key]}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
          {loadingMore && rows.length > 0 && (
            <tr className="ui-datatable__morerow">
              <td colSpan={columns.length || 1}>
                <div className="ui-datatable__more"><span className="spin" aria-hidden="true" /> Loading more…</div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
